<template>
  <div class="deals-filter card">
    <!-- Search query -->
    <div class="deals-filter__search mb-1">
      <b-input-group>
        <b-input-group-prepend is-text>
          <feather-icon icon="SearchIcon" size="14" />
        </b-input-group-prepend>
        <b-form-input
          v-model="query"
          :placeholder="$t('creator.searchDeals')"
          debounce="300"
          @update="emitFilters"
        />
        <b-input-group-append v-if="query" is-text class="cursor-pointer" @click="clearQuery">
          <feather-icon icon="XIcon" size="14" />
        </b-input-group-append>
      </b-input-group>
    </div>

    <b-row class="m-0 deals-filter__controls">
      <!-- Network checkboxes -->
      <b-col class="col-12 col-md-7 p-0 pr-md-1 mb-1 mb-md-0">
        <p class="deals-filter__label mb-05">{{ $t('creator.filterByNetwork') }}</p>
        <div class="deals-filter__networks">
          <div
            v-for="net in available_networks"
            :key="net"
            class="deals-filter__network-chip"
            :class="{ 'deals-filter__network-chip--active': selected_networks.includes(net) }"
            @click="toggleNetwork(net)"
          >
            <div :class="`network-icon-mini ${getIconColor(net, false)}`">
              <b-img :src="getIconWhite(net)" fluid />
            </div>
            <span class="ml-05 text-capitalize">{{ net }}</span>
          </div>
        </div>
      </b-col>

      <!-- Price range -->
      <b-col class="col-12 col-md-5 p-0">
        <p class="deals-filter__label mb-05">{{ $t('creator.filterByPrice') }}</p>
        <div class="d-flex align-items-center">
          <b-form-input
            v-model.number="price_min"
            type="number"
            min="0"
            step="0.01"
            :placeholder="$t('creator.priceMinFilter')"
            class="deals-filter__price-input"
            @input="emitFilters"
            @wheel.native.prevent
          />
          <span class="mx-05 text-muted">—</span>
          <b-form-input
            v-model.number="price_max"
            type="number"
            min="0"
            step="0.01"
            :placeholder="$t('creator.priceMaxFilter')"
            class="deals-filter__price-input"
            @input="emitFilters"
            @wheel.native.prevent
          />
        </div>
      </b-col>
    </b-row>

    <!-- Active filters badges -->
    <div v-if="has_active_filters" class="deals-filter__active mt-1 d-flex align-items-center flex-wrap">
      <span class="text-muted small mr-1">{{ $t('creator.activeFilters') }}:</span>
      <b-badge
        v-if="query"
        variant="light-primary"
        class="mr-05 mb-05 deals-filter__badge"
        @click="clearQuery"
      >
        "{{ query }}" <feather-icon icon="XIcon" size="10" class="ml-02" />
      </b-badge>
      <b-badge
        v-for="net in selected_networks"
        :key="net"
        variant="light-primary"
        class="mr-05 mb-05 deals-filter__badge text-capitalize"
        @click="toggleNetwork(net)"
      >
        {{ net }} <feather-icon icon="XIcon" size="10" class="ml-02" />
      </b-badge>
      <b-badge
        v-if="price_min || price_max"
        variant="light-primary"
        class="mr-05 mb-05 deals-filter__badge"
        @click="clearPrice"
      >
        $ {{ price_min || 0 }} — {{ price_max || '∞' }} <feather-icon icon="XIcon" size="10" class="ml-02" />
      </b-badge>
      <b-link class="small ml-auto" @click="clearAll">{{ $t('creator.clearFilters') }}</b-link>
    </div>
  </div>
</template>

<script>
import { BFormInput, BInputGroup, BInputGroupPrepend, BInputGroupAppend, BCol, BRow, BBadge, BImg, BLink } from 'bootstrap-vue';
import { getIconWhite, getIconColor } from '@/libs/utils/icons';

export default {
  name: 'DealsFilter',
  components: {
    BFormInput,
    BInputGroup,
    BInputGroupPrepend,
    BInputGroupAppend,
    BCol,
    BRow,
    BBadge,
    BImg,
    BLink,
  },
  data() {
    return {
      query: '',
      selected_networks: [],
      price_min: null,
      price_max: null,
    };
  },
  computed: {
    available_networks() {
      return ['instagram', 'youtube', 'tiktok', 'twitter', 'facebook', 'linkedin', 'spotify', 'pinterest', 'twitch', 'blog', 'snapchat', 'telegram', 'whatsapp'];
    },
    has_active_filters() {
      return !!(this.query || this.selected_networks.length || this.price_min || this.price_max);
    },
  },
  methods: {
    getIconWhite,
    getIconColor,
    toggleNetwork(net) {
      const idx = this.selected_networks.indexOf(net);
      if (idx === -1) {
        this.selected_networks.push(net);
      } else {
        this.selected_networks.splice(idx, 1);
      }
      this.emitFilters();
    },
    clearQuery() {
      this.query = '';
      this.emitFilters();
    },
    clearPrice() {
      this.price_min = null;
      this.price_max = null;
      this.emitFilters();
    },
    clearAll() {
      this.query = '';
      this.selected_networks = [];
      this.price_min = null;
      this.price_max = null;
      this.emitFilters();
    },
    emitFilters() {
      this.$emit('filter_change', {
        query: this.query,
        networks: this.selected_networks,
        price_min: this.price_min,
        price_max: this.price_max,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.deals-filter {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 1rem;
  margin-bottom: 1rem;
  
  &__label {
    font-size: 0.8rem;
    font-weight: 600;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  &__networks {
    display: flex;
    flex-wrap: wrap;
    gap: 0.4rem;
  }

  &__network-chip {
    display: inline-flex;
    align-items: center;
    border: 1.5px solid #e5e7eb;
    border-radius: 20px;
    padding: 3px 10px 3px 4px;
    cursor: pointer;
    font-size: 0.78rem;
    background: #fff;
    transition: all 0.15s ease;
    user-select: none;

    &:hover {
      border-color: #a0aec0;
      background: #f3f4f6;
    }

    &--active {
      border-color: var(--primary, #7367f0);
      background: rgba(115, 103, 240, 0.08);
      color: var(--primary, #7367f0);
      font-weight: 600;
    }
  }

  &__price-input {
    font-size: 0.875rem;
    height: 36px;
  }

  &__badge {
    cursor: pointer;
    font-size: 0.75rem;
    font-weight: 500;

    &:hover {
      opacity: 0.8;
    }
  }
}

.network-icon-mini {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  img {
    width: 11px;
    height: 11px;
    object-fit: contain;
  }
}

.ml-02 { margin-left: 0.2rem; }
.ml-05 { margin-left: 0.3rem; }
.mb-05 { margin-bottom: 0.3rem; }
.mr-05 { margin-right: 0.3rem; }
.mx-05 { margin-left: 0.3rem; margin-right: 0.3rem; }
.cursor-pointer { cursor: pointer; }
</style>
